import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertTriangle, Utensils, Info } from 'lucide-react';
import { publicEvenementielApi } from '../../lib/api';

interface TableInfo {
    event_label: string;
    table_number: string;
    guest_count: number;
}

const PoweredByFooter: React.FC = () => (
    <a href="https://l-iamani.com" target="_blank" rel="noopener noreferrer"
        className="mt-5 inline-flex items-center gap-1 text-[11px] text-content-muted hover:text-content transition-colors">
        Propulsé par <span className="font-bold">L'IAmani</span>
    </a>
);

/**
 * Page publique scannée via le QR posé sur une table, le Jour J (maquette Suivi Privatisation, 18f).
 *
 * LE CONVIVE SE RECONNAÎT PAR SON ADRESSE EMAIL. Il se cherchait jusqu'ici dans la liste complète des
 * invités de la soirée — ce qui voulait dire que n'importe qui scannant un QR collé sur une table voyait
 * tout le monde, adresses masquées comprises. La maquette pose la règle inverse : « aucun compte, aucun
 * mot de passe — le QR de la table plus une adresse email, c'est tout ». On ne divulgue donc plus rien :
 * c'est le convive qui apporte son adresse, et le serveur se contente de dire s'il la reconnaît.
 *
 * Trois temps, comme la maquette : il vérifie la table, il donne son email, son menu s'affiche. Et la
 * cuisine est prévenue au même instant — pas de saisie intermédiaire, pas de feuille à remonter.
 */
export const TableCheckinPage: React.FC = () => {
    const { publicToken, tableNumber } = useParams<{ publicToken: string; tableNumber: string }>();
    const [phase, setPhase] = React.useState<'loading' | 'notfound' | 'ready'>('loading');
    const [info, setInfo] = React.useState<TableInfo | null>(null);
    const [email, setEmail] = React.useState('');
    const [envoi, setEnvoi] = React.useState(false);
    const [installe, setInstalle] = React.useState<{ prenom: string; recap: { category: string; name: string }[]; declarations: string[] } | null>(null);
    const [notResponded, setNotResponded] = React.useState<string | null>(null);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        if (!publicToken || !tableNumber) return;
        publicEvenementielApi.getTableCheckinInfo(publicToken, tableNumber).then((data: any) => {
            if (data?.error) { setPhase('notfound'); return; }
            setInfo(data);
            setPhase('ready');
        }).catch(() => setPhase('notfound'));
    }, [publicToken, tableNumber]);

    const emailValide = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());

    const sInstaller = async () => {
        if (!publicToken || !tableNumber || !emailValide) return;
        setEnvoi(true);
        setError('');
        try {
            const res: any = await publicEvenementielApi.submitTableCheckin(publicToken, tableNumber, { email: email.trim() });
            if (res?.error) { setError(res.error); return; }
            if (res?.not_responded) { setNotResponded(res.message || ''); return; }
            setInstalle({
                prenom: res?.prenom || '',
                recap: Array.isArray(res?.recap) ? res.recap : [],
                declarations: Array.isArray(res?.declarations) ? res.declarations : [],
            });
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la déclaration.');
        } finally {
            setEnvoi(false);
        }
    };

    if (phase === 'loading') return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-content-muted" size={28} /></div>;
    if (phase === 'notfound' || !info) return (
        <div className="min-h-screen flex items-center justify-center p-6 text-center">
            <p className="text-content-muted">Ce lien n'est plus valide.</p>
        </div>
    );

    // Un convive qui n'a jamais répondu au menu ne peut pas se déclarer assis : il faut voir les
    // possibilités avec la cuisine, et une confirmation ici masquerait le problème.
    if (notResponded) return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center gap-3">
            <AlertTriangle size={40} className="text-amber-500" />
            <h1 className="text-lg font-bold text-content">Réponse non reçue</h1>
            <p className="text-sm text-content-muted max-w-xs">{notResponded}</p>
            <PoweredByFooter />
        </div>
    );

    if (installe) return (
        <div className="min-h-screen flex flex-col items-center p-6">
            <div className="w-full max-w-sm flex flex-col items-center text-center">
                <CheckCircle2 size={40} className="text-brand-500" />
                <h1 className="mt-3 text-[21px] font-bold tracking-[-0.02em] text-content">Vous êtes installé</h1>
                <p className="mt-1 text-[12.5px] text-content-muted">
                    Table {info.table_number} · la cuisine est prévenue
                </p>

                {installe.prenom && (
                    <p className="mt-6 text-[15px] font-bold text-content self-start">Bonsoir {installe.prenom}</p>
                )}

                <div className="mt-2 w-full p-4 rounded-2xl bg-surface border border-border text-left">
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[.09em] text-content-muted mb-3">
                        <Utensils size={12} /> Votre menu de ce soir
                    </div>
                    {installe.recap.length === 0 ? (
                        <p className="text-[12.5px] text-content-muted italic">Aucun plat enregistré.</p>
                    ) : (
                        <div className="flex flex-col divide-y divide-border">
                            {installe.recap.map((m) => (
                                <div key={m.category} className="py-2 first:pt-0 last:pb-0">
                                    <div className="text-[10px] font-bold uppercase tracking-[.08em] text-content-muted">{m.category}</div>
                                    <div className="text-[13px] text-content">{m.name}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {installe.declarations.length > 0 && (
                    <div className="mt-3 w-full p-4 rounded-2xl bg-surface border border-border text-left">
                        <div className="text-[10px] font-bold uppercase tracking-[.09em] text-content-muted mb-2">Ce que vous avez déclaré</div>
                        <p className="text-[13px] text-content">
                            {installe.declarations.join(' · ')} — pris en compte en cuisine.
                        </p>
                    </div>
                )}

                {/* Le convive ne corrige rien lui-même une fois assis : c'est le serveur qui le fait depuis
                    le passe, sinon une modification de dernière minute ne remonterait à personne. */}
                <p className="mt-3 text-[11.5px] text-content-muted flex items-start gap-1.5 text-left">
                    <Info size={13} className="shrink-0 mt-px" />
                    Une erreur ? Prévenez un serveur, il corrigera depuis le passe.
                </p>
                <PoweredByFooter />
            </div>
        </div>
    );

    return (
        <div className="min-h-screen flex flex-col items-center p-6">
            <div className="w-full max-w-sm flex flex-col items-center text-center">
                <p className="text-[11px] font-bold uppercase tracking-[.09em] text-content-muted">{info.event_label || 'Bienvenue'}</p>

                <div className="mt-4 w-full p-5 rounded-2xl bg-surface border border-border">
                    <div className="text-[34px] font-bold tracking-[-0.03em] text-content leading-none tabular-nums">{info.table_number}</div>
                    <p className="mt-2 text-[14px] font-bold text-content">Vous êtes à la table {info.table_number}</p>
                    <p className="mt-1 text-[12px] text-content-muted">Vérifiez que c'est bien le numéro affiché sur votre table.</p>
                    <p className="mt-2 text-[11.5px] text-content-muted">
                        <span className="font-semibold">Ce n'est pas la bonne ?</span> Scannez simplement le QR de la table où vous vous installez.
                    </p>
                </div>

                <div className="mt-3 w-full p-5 rounded-2xl bg-surface border border-border text-left">
                    <div className="flex items-center justify-between gap-2 mb-3">
                        <span className="text-[14px] font-bold text-content">Qui êtes-vous ?</span>
                        <span className="text-[10.5px] font-bold uppercase tracking-[.08em] text-content-muted whitespace-nowrap">Étape 2 sur 2</span>
                    </div>
                    <label className="block">
                        <span className="block text-[10px] font-bold uppercase tracking-[.08em] text-content-muted mb-1.5">Votre adresse email</span>
                        <input
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && emailValide && !envoi) sInstaller(); }}
                            placeholder="vous@exemple.fr"
                            className="w-full h-11 bg-surface-soft border border-border rounded-[11px] px-3.5 text-[15px] text-content outline-none focus:border-content"
                        />
                    </label>
                    <button
                        type="button"
                        onClick={sInstaller}
                        disabled={!emailValide || envoi}
                        className="mt-3 w-full h-11 rounded-full bg-brand-600 text-brand-fg text-[14px] font-bold disabled:opacity-40 hover:bg-brand-700 transition-colors"
                    >
                        {envoi ? 'Un instant…' : "Je m'installe ici"}
                    </button>
                    <p className="mt-2 text-[11.5px] text-content-muted text-center">Aucun compte à créer. Rien d'autre à remplir.</p>
                    {error && <p className="mt-3 text-[12px] text-state-danger font-semibold">{error}</p>}
                </div>

                <PoweredByFooter />
            </div>
        </div>
    );
};
